import React from 'react';
import {
  COLORS,
  SPACING,
  FONT_SIZE,
  FONT_WEIGHT,
  BORDER_RADIUS,
  SHADOWS,
  TOUCH_TARGET,
} from '../../constants/theme';

/**
 * UserProfileCard Component
 * Mobile-optimized card for displaying user profile information
 *
 * @param {Object} profile - The user profile object
 * @param {Array} organizations - List of organizations
 * @param {Array} teams - List of teams
 * @param {Function} onEdit - Callback when "Edit" button is clicked
 * @param {Function} onDelete - Callback when "Delete" button is clicked
 * @param {Function} onImpersonate - Optional callback when "Impersonate" button is clicked (super_admin only)
 * @param {boolean} showImpersonate - Whether to show the impersonate button
 */
const UserProfileCard = ({
  profile,
  organizations,
  teams,
  onEdit,
  onDelete,
  onImpersonate,
  showImpersonate,
}) => {
  const organization = organizations.find((org) => org.id === profile.organization_id);
  const team = teams.find((t) => t.id === profile.team_id);

  const getRoleBadgeStyle = (role) => {
    const baseStyle = {
      display: 'inline-block',
      padding: `${SPACING.xs} ${SPACING.sm}`,
      borderRadius: BORDER_RADIUS.full,
      fontSize: FONT_SIZE.sm,
      fontWeight: FONT_WEIGHT.semibold,
      textTransform: 'uppercase',
    };

    switch (role?.toLowerCase()) {
      case 'admin':
        return {
          ...baseStyle,
          backgroundColor: '#e3f2fd',
          color: '#1976d2',
        };
      case 'coach':
        return {
          ...baseStyle,
          backgroundColor: '#f3e5f5',
          color: '#7b1fa2',
        };
      case 'player':
        return {
          ...baseStyle,
          backgroundColor: '#e8f5e9',
          color: '#388e3c',
        };
      case 'parent':
        return {
          ...baseStyle,
          backgroundColor: '#fff3e0',
          color: '#f57c00',
        };
      case 'super_admin':
        return {
          ...baseStyle,
          backgroundColor: '#f3e5f5',
          color: '#4a148c',
        };
      default:
        return {
          ...baseStyle,
          backgroundColor: COLORS.gray[400],
          color: COLORS.white,
        };
    }
  };

  const styles = {
    card: {
      backgroundColor: COLORS.white,
      borderRadius: BORDER_RADIUS.md,
      boxShadow: SHADOWS.md,
      padding: SPACING.lg,
      marginBottom: SPACING.md,
      border: `1px solid ${COLORS.gray[300]}`,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: SPACING.md,
      paddingBottom: SPACING.md,
      borderBottom: `1px solid ${COLORS.gray[200]}`,
    },
    title: {
      fontSize: FONT_SIZE.lg,
      fontWeight: FONT_WEIGHT.semibold,
      color: COLORS.gray[700],
      flex: 1,
      marginRight: SPACING.sm,
    },
    body: {
      display: 'flex',
      flexDirection: 'column',
      gap: SPACING.sm,
      marginBottom: SPACING.md,
      fontSize: FONT_SIZE.base,
    },
    label: {
      fontWeight: FONT_WEIGHT.semibold,
      color: COLORS.gray[600],
      fontSize: FONT_SIZE.md,
    },
    value: {
      color: COLORS.gray[700],
      fontSize: FONT_SIZE.md,
    },
    actions: {
      display: 'flex',
      flexDirection: 'column',
      gap: SPACING.sm,
    },
    button: {
      width: '100%',
      padding: SPACING.md,
      fontSize: FONT_SIZE.base,
      fontWeight: FONT_WEIGHT.medium,
      border: 'none',
      borderRadius: BORDER_RADIUS.sm,
      cursor: 'pointer',
      textAlign: 'center',
      minHeight: TOUCH_TARGET.minimum,
    },
    primaryButton: {
      backgroundColor: COLORS.primary,
      color: COLORS.white,
    },
    dangerButton: {
      backgroundColor: COLORS.danger,
      color: COLORS.white,
    },
    impersonateButton: {
      backgroundColor: '#6f42c1',
      color: COLORS.white,
    },
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>{profile.full_name || 'Not set'}</div>
        <span style={getRoleBadgeStyle(profile.role)}>{profile.role || 'N/A'}</span>
      </div>

      <div style={styles.body}>
        <div>
          <div style={styles.label}>Email</div>
          <div style={styles.value}>{profile.email}</div>
        </div>
        <div>
          <div style={styles.label}>Organization</div>
          <div style={styles.value}>{organization?.name || 'Not set'}</div>
        </div>
        <div>
          <div style={styles.label}>Team</div>
          <div style={styles.value}>{team?.name || 'Not assigned'}</div>
        </div>
        {profile.phone && (
          <div>
            <div style={styles.label}>Phone</div>
            <div style={styles.value}>{profile.phone}</div>
          </div>
        )}
      </div>

      <div style={styles.actions}>
        <button
          style={{ ...styles.button, ...styles.primaryButton }}
          onClick={() => onEdit(profile)}
        >
          Edit
        </button>
        <button
          style={{ ...styles.button, ...styles.dangerButton }}
          onClick={() => onDelete(profile)}
        >
          Delete
        </button>
        {showImpersonate && onImpersonate && (
          <button
            style={{ ...styles.button, ...styles.impersonateButton }}
            onClick={() => onImpersonate(profile)}
          >
            Impersonate
          </button>
        )}
      </div>
    </div>
  );
};

export default UserProfileCard;
